"use client";

import { type CreateToastArgs, createToaster } from "@decibeltrade/web-core/components";
import dynamic from "next/dynamic";
import { useEffect } from "react";
import { css } from "styled-system/css";

import { useTransferProgressHidden } from "@/contexts/transfer-progress-visibility";
import { useAuthState } from "@/hooks/use-auth-state";
import { addressComparisonKey } from "@/utils/address";

import { AppUpdateNotification } from "./app-update-notification";
import { useHasTransfers } from "./transfer-progress/has-transfers";

/**
 * The transfer-progress card is the root layout's only path into the cross-chain bridge stack
 * (Wormhole, Solana web3) — roughly 300 KB gzipped that every route was paying for on first load
 * to render a card that only exists during a transfer. Transfers live in localStorage, so there
 * is nothing to server-render either.
 *
 * Mounted only when there is a card to draw (see `Toaster`), not merely lazily: `React.lazy`
 * invokes its loader on first *render*, so rendering this unconditionally would fetch and parse
 * the chunk on every route regardless — off the critical path, but still fetched.
 */
const TransferProgressContainer = dynamic(
  () =>
    import("./transfer-progress/transfer-progress-container").then(
      (m) => m.TransferProgressContainer,
    ),
  { ssr: false },
);

const [BaseToaster, baseToast] = createToaster();

const accountScopedToastIds = new Set<string>();

// Module-level so it survives /auth/* unmounts (where shouldSkipProviders strips the dismisser).
let lastReconciledAccount: string | undefined;

export type AppCreateToastArgs =
  | (CreateToastArgs & { accountScoped?: false | undefined })
  /** `accountScoped: true` dismisses the toast on wallet switch — see `AccountScopedToastDismisser`. Requires `id`. */
  | (CreateToastArgs & { accountScoped: true; id: string });

/**
 * When invoked, a toast message will be displayed.
 * The `Toaster` must be mounted in the component tree for the toast message to be displayed.
 *
 * For per-account persistent toasts, see `accountScoped` on `AppCreateToastArgs`.
 */
export const toast = Object.assign(
  ({ accountScoped, ...args }: AppCreateToastArgs) => {
    if (accountScoped && args.id) accountScopedToastIds.add(args.id);
    baseToast(args);
  },
  {
    /** Dismiss by id, or all toasts if no id — keeps the scoped-id Set in sync. */
    dismiss: (id?: string) => {
      if (id) accountScopedToastIds.delete(id);
      else accountScopedToastIds.clear();
      baseToast.dismiss(id);
    },
    update: baseToast.update,
    getVisibleToasts: baseToast.getVisibleToasts,
  },
);

/**
 * Dismisses `accountScoped` toasts on wallet switch; also prunes the tracked-id Set when
 * ArkUI's built-in close button removes a toast outside our wrapper.
 *
 * Why a React component, not a module-level subscription: the derived auth state is
 * composed by `useAuthState` from three sources — two React Contexts (`useDecibelWallet`,
 * `useWriteDexSdk`) plus localStorage-backed `useGetImportedSessionKey`. The Context
 * values aren't reachable outside React, so the composition has to happen here.
 *
 * Why `state === "LOADING"` is skipped: `useAuthState` flicks through `LOADING` during
 * wallet auto-reconnect on page refresh, and clearing then re-showing the toast would
 * flicker. The window where this hides a real switch is sub-second and rare in practice.
 */
export function AccountScopedToastDismisser() {
  const auth = useAuthState();
  useEffect(() => {
    if (auth.state === "LOADING") return;
    if (addressComparisonKey(lastReconciledAccount) === addressComparisonKey(auth.accountAddress)) {
      return;
    }
    accountScopedToastIds.forEach((id) => baseToast.dismiss(id));
    accountScopedToastIds.clear();
    lastReconciledAccount = auth.accountAddress;
  }, [auth.state, auth.accountAddress]);
  // No deps: prune ids ArkUI dismissed via its close button (which bypasses our wrapper).
  // In practice this runs at the same cadence as the dismiss-on-switch effect, since
  // the dismisser only re-renders when `useAuthState` ticks.
  useEffect(() => {
    if (accountScopedToastIds.size === 0) return;
    const visible = new Set(
      baseToast
        .getVisibleToasts()
        .map((t) => t.id)
        .filter((id): id is string => !!id),
    );
    for (const id of accountScopedToastIds) {
      if (!visible.has(id)) accountScopedToastIds.delete(id);
    }
  });
  return null;
}

/** Renders all toast messages, transfer progress, and the app update notification. */
export function Toaster() {
  // Exactly the two conditions `TransferProgressContainer` already checks before returning null,
  // hoisted out so the chunk is fetched when it has something to render rather than on hydration.
  // Kept in step with the guard at the top of that component.
  const { state, ownerWalletAddress } = useAuthState();
  const hasTransfers = useHasTransfers(ownerWalletAddress);
  const showTransferProgress = state === "WALLET_CONNECTED" && hasTransfers;
  // Hidden rather than unmounted, so the cards keep their state behind Fun's checkout.
  const isTransferProgressHidden = useTransferProgressHidden();

  return (
    <div
      className={css({
        zIndex: "toast!",
        position: "fixed",
        bottom: {
          base: "[calc(60px + max(env(safe-area-inset-bottom, 0px), 12px))]",
          desktop: "[env(safe-area-inset-bottom, 0px)]",
        },
        insetInlineEnd: "[env(safe-area-inset-right, 0px)]",
      })}
    >
      <div className={css({ position: "relative" })}>
        <BaseToaster
          className={css({ position: "absolute!", bottom: "16!", insetInlineEnd: "16!" })}
        />
      </div>
      {showTransferProgress && (
        <div hidden={isTransferProgressHidden}>
          <TransferProgressContainer className={transferCardStyles} />
        </div>
      )}
      <AppUpdateNotification className={css({ mb: "16", marginInlineEnd: "16" })} />
    </div>
  );
}

const transferCardStyles = css({
  mb: "16",
  marginInlineEnd: "16",
  w: "[356px]",
  maxW: "[calc(100vw - 32px)]",
});
